import { driveSubFromDto, drivePlanFromDto, reducePlans, createPlansHash, preparePlans } from '@/modules/drive-dto';
import accountService from '@/modules/account-service';
import Vue from 'vue';

const state = {
	plans: [],
	allPlans: [],
	newPlan: null,
	subscriptionRenews: true,
	planCards: [],
	mappedPlans: {},
	currentSubscription: null,
	currentPlanCopy: null,
	reviewPlanCards: null
};

const ONE_OFF_PLANS = [
	{
		active: false,
		oneTimeOffer: true,
		amount: 0,
		customBilling: false,
		details: {
			storageSizeLimit: 200000000
		},
		hasTrial: false,
		id: "pln_tekdriveportal_promotional_v1",
		interval: "monthly",
		name: "TekDrive Portal User Plan 200",
		paymentMethodRequired: true
	}
];

const getters = {
	driveSubscriptions: (state, getters, rootState, rootGetters) => {
		return rootState.subscription.subscriptions && rootState.subscription.subscriptions.filter(sub=> {
			return sub && sub.plan.id.includes('tekdrive');
		}).map((sub) => driveSubFromDto(sub, state.allPlans));
	},

	staticDriveSubs : (state, getters) => {
		return getters.driveSubscriptions && getters.driveSubscriptions.filter(sub=> {
			return sub.isPrepaid || sub.isTrial || sub.isOneTimeOffer;
		});
	},

	driveSubscription: (state, getters) => {
		return getters && getters.driveSubscriptions && getters.driveSubscriptions.find(sub=> {
			return !sub.isTrial && !sub.isPrepaid && !sub.isOneTimeOffer;
		});
	},

	trialDriveSub: (state, getters) => {
		return getters.driveSubscriptions && getters.driveSubscriptions.find(sub=> {
			return sub.isTrial;
		});
	},

	prepaidDriveSub: (state, getters) => {
		return getters.driveSubscriptions && getters.driveSubscriptions.find(sub=> {
			return sub.isPrepaid;
		});
	},

	prepaidDriveSubs: (state, getters) => {
		return getters.driveSubscriptions && getters.driveSubscriptions.filter(sub=> {
			return sub.isPrepaid;
		});
	},

	highestPlan: (state, getters) => {
		const sortedDriveSubs = getters.driveSubscriptions.sort((a, b) => b.plan.details.storageSizeLimit.value - a.plan.details.storageSizeLimit.value);
		if (sortedDriveSubs.length === 0) {
			return null;
		}
		return sortedDriveSubs[0].plan;
	}
};

const mutations = {

	'GET_SUBSCRIPTION_SUCCESS': (state, current) => {
		state.getSubscriptionStatus = {
			success: true
		};

		state.currentSubscription = current;
	},

	'SET_SUBSCRIPTION' : (state, current) => {
		state.getSubscriptionStatus = {
			success: true
		};

		if (!state.newPlan && current) { // new plan implies that the user is returning from payment screen
			state.subscriptionRenews = current.renews;
		}

		state.currentSubscription = current;
	},

	'SET_PLANS': (state, { plans, planCards, mappedPlans }) => {
		state.plans = plans;
		state.mappedPlans = mappedPlans;
		state.planCards = planCards;
	},

	'SET_ALL_PLANS': (state, plans) => {
		state.allPlans = plans;
	},

	'SET_RENEWAL_FLAG': (state, renews) => {
		state.subscriptionRenews = renews;
	},

	'SET_NEW_PLAN': (state, { plan, currentPlan, reviewPlanCards}) => {
		state.newPlan = plan;
		state.currentPlanCopy = currentPlan ? Object.assign({}, currentPlan) : null;
		state.reviewPlanCards = reviewPlanCards;
	},

	'RESET_STATE': (state) => {
		if (state.currentSubscription) {
			state.subscriptionRenews = state.currentSubscription.renews;
		}
		state.newPlan = null;
		state.currentPlanCopy = null;
		state.reviewPlanCards = null;
	},

	'TEAR_DOWN': (state) => {
		state.currentSubscription = null;
	},

};

function getSubById(subs, subId) {
	return subs && subs.find(sub=> {
		return sub && sub.id === subId;
	});
}

function deepCopy(obj) {
	return JSON.parse(JSON.stringify(obj));
}

const actions = {
	'GET_SUBSCRIPTION_REQUEST' : ({ commit, dispatch, getters, rootGetters, rootState }, {product , subId}) => {

		if (state.allPlans.length) {
			const plansCopy = deepCopy(state.allPlans);

			const activePlans = plansCopy.filter(plan => plan.active);
			dispatch('SET_PLANS', activePlans);

			const current = subId ? getSubById(getters.driveSubscriptions, subId) : getters.driveSubscription;
			if (!current && subId){
				return Promise.reject(new Error('Invalid Subscription ID'));
			}
			const currentPlanInactive = current && !current.plan.active;

			// a work-around to present old plans with accurate info
			if (!subId && currentPlanInactive) {
				const fullPlanId = current.plan.id + '_' + current.plan.interval;
				const activeOrCurrentPlans = plansCopy.filter(plan => plan.active || plan.id === fullPlanId);

				dispatch('SET_PLANS', activeOrCurrentPlans);
			} else if (subId && currentPlanInactive) {
				// just get all plans for static subscription view
				dispatch('SET_PLANS', plansCopy);
			}

			commit('SET_SUBSCRIPTION', current);
			return Promise.resolve();
		}

		return accountService.getPlansV2('tekdrive')
			.then((response) => {
				const planData = response.concat(ONE_OFF_PLANS);

				//ensure we have all plans new and old
				commit('SET_ALL_PLANS', deepCopy(planData));

				const activePlans = planData.filter(plan => plan.active);
				dispatch('SET_PLANS', activePlans);

				dispatch('subscription/GET_TEK_SUBSCRIPTIONS_REQUEST', null, {root:true})
					.then(subs => {
						//subId would mean its prepaid
						const current = subId ? getSubById(getters.driveSubscriptions, subId) : getters.driveSubscription;
						if (!current && subId){
							return Promise.reject(new Error('Invalid Subscription ID'));
						}
						const currentPlanInactive = current && !current.plan.active;
						// a work-around to present old plans with accurate info in manage subscription view
						if (!subId && currentPlanInactive) {
							const fullPlanId = current.plan.id + '_' + current.plan.interval;
							const activeOrCurrentPlans = planData.filter(plan => plan.active || plan.id === fullPlanId);

							dispatch('SET_PLANS', activeOrCurrentPlans);
						} else if (subId && currentPlanInactive) {
							// just get all plans for static subscription view
							dispatch('SET_PLANS', planData);
						}
						commit('SET_SUBSCRIPTION', current);
						return subs;
					});
			})
			.catch(error => {
				return error;
			});
	},



	['CHANGE_SUBSCRIPTION_REQUEST']: ({ commit, dispatch, rootGetters, getters }, payload) => {
		return accountService.changeSubscription(payload)
			.then(response => {
				dispatch('subscription/CHANGE_SUBSCRIPTION_SUCCESS', response, {root:true});
				// do a get on plans to stay current
				accountService.getPlansV2('tekdrive')
					.then((response) => {
						const planData = response.concat(ONE_OFF_PLANS);
						commit('SET_ALL_PLANS', planData);

						const activePlans = planData.filter(plan => plan.active);
						dispatch('SET_PLANS', activePlans);
					});

				commit('SET_SUBSCRIPTION', getters.driveSubscription);
				return Promise.resolve();
			})
			.catch(error => {
				return Promise.reject(error);
			});
	},



	['ADD_SUBSCRIPTION_REQUEST']: ({ commit, dispatch, rootGetters, getters }, payload) => {
		return accountService.addSubscription(payload)
			.then(response => {
				dispatch('subscription/ADD_SUBSCRIPTION_SUCCESS', response, {root:true});

				// do a get on plans to stay current
				accountService.getPlansV2('tekdrive')
					.then((response) => {
						const planData = response.concat(ONE_OFF_PLANS);
						commit('SET_ALL_PLANS', planData);

						const activePlans = planData.filter(plan => plan.active);
						dispatch('SET_PLANS', activePlans);
					});

				commit('SET_SUBSCRIPTION', getters.driveSubscription);
				return Promise.resolve();
			})
			.catch(error => {
				return Promise.reject(error);
			});
	},

	'SET_PLANS' : ({ commit, state }, planData) => {
		const plans = preparePlans(planData);

		commit('SET_PLANS', { plans, planCards: reducePlans(plans), mappedPlans: createPlansHash(plans) });
	},

	'SET_RENEWAL_FLAG' : ({ commit }, renews) => {
		commit('SET_RENEWAL_FLAG', renews);
	},

	'SET_NEW_PLAN' : ({ commit }, plan) => {
		commit('SET_NEW_PLAN', plan);
	},

	'RESET_STATE' : ({ commit }) => {
		commit('RESET_STATE');
	},

	'TEAR_DOWN' : ({ commit, dispatch }) => {
		commit('TEAR_DOWN');
	},

};

// return accountService.getSubscriptions()
// 	.then(response => {
// 		let subs = response.data.subscriptions;
// 		commit(GET_SUBSCRIPTIONS_SUCCESS, subs);
//
// 		if (getters.scopeSubscriptions.length) {
// 			dispatch(SET_MASTER_SCOPE_SUB);
// 		}
//
// 		return Promise.resolve(subs);
// 	})
// 	.catch(error => {
// 		commit(GET_SUBSCRIPTIONS_ERROR);
// 		return Promise.reject(error);
// 	});


export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/drive.js